import { MiscService } from '@app/core/services/misc.service';
import { HttpClient } from '@angular/common/http';
import { Component, ChangeDetectorRef, ChangeDetectionStrategy, OnInit, OnDestroy, ViewChild, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FormlyModule } from '@ngx-formly/core';
import { TranslateModule } from '@ngx-translate/core';
import { IonSpinner } from '@ionic/angular/standalone';
import { TreeModule, ITreeOptions, IActionMapping, TreeComponent, TreeNode, ITreeState } from '@ali-hm/angular-tree-component';
import { FormlyFieldAnyComponent } from '@app/shared/components/formly-components/formly-field-any/formly-field-any.component';
import { SubSink } from 'subsink';
import { TreeNodeData } from '@app/shared/components/formly-components/formly-field-tree-builder/formly-field-tree-builder.component';
import { environment } from '@env/environment';
import { ApiResponse } from '@app/core/models/user.model';
import { ContextService } from '@app/core/services/context.service';
import { TreeAttachment } from '@app/core/interfaces/formly-forms.interface';
import { DynamicIconComponent } from '@app/shared/components/dynamic-icon';

@Component({
  selector: 'app-formly-tree-level',
  templateUrl: './formly-field-tree-level.component.html',
  styleUrls: ['./formly-field-tree-level.component.scss'],
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    IonSpinner, 
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    FormlyModule,
    TranslateModule,
    TreeModule,
    DynamicIconComponent
  ],
})
export class FormlyTreeLevelComponent extends FormlyFieldAnyComponent implements OnInit, OnDestroy {
  @ViewChild(TreeComponent) tree!: TreeComponent;
  
  public nodes: TreeNodeData[] = [];
  public treeState: ITreeState = {};  
  public isLoading: boolean = true;
  public selectedNodeId:string | null = null;
  public treeOptions: ITreeOptions = {
    allowDrag: (element) => false,
    allowDrop: (element, { parent, index }) => false,
    actionMapping: this.getActionMapping(),
    idField: 'id',
    displayField: 'name',
    childrenField: 'children',
    nodeHeight: 23,
    levelPadding: 20,
    animateExpand: true,
    animateSpeed: 30,
    animateAcceleration: 1.2,
  };
  
  private subsink = new SubSink();
  private miscService = inject(MiscService);
  private http = inject(HttpClient);
  private contextService  = inject(ContextService);

  constructor(private changeDetectorRef: ChangeDetectorRef) {
    super();
  }

  async ngOnInit(): Promise<void> {
    this.subsink.sink = this.formControl.valueChanges.subscribe(value => {
      this.updateLocalValue(value);
    });

    await this.getSourceData();

    this.updateLocalValue(this.formControl.value);
    if (this.selectedNodeId && this.nodes.length > 0) {
      // Extend branches leading to selected node
      this.treeState.expandedNodeIds = this.miscService.getExpandedNodeIdsForNodeId(this.nodes[0], this.selectedNodeId);
      this.treeState.expandedNodeIds[this.nodes[0].id] = false; // But collapse the root node itself
    } else {
      // See if we can automatically extend root node
      if (this.nodes.length > 0) {
        this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
        this.treeState.expandedNodeIds = { [this.nodes[0].id]: true };
      }
    }
    this.changeDetectorRef.markForCheck();
    this.isLoading = false;
  }

  ngOnDestroy(): void {
    this.subsink.unsubscribe();
  }

  private updateLocalValue(value: any) {
    this.selectedNodeId = value;
    if (this.tree && this.tree.treeModel) {
      const nodeToActivate = this.tree.treeModel.getNodeById(value);
      if (nodeToActivate) {
        this.tree.treeModel.setActiveNode(nodeToActivate, false);
      }
    }
    this.changeDetectorRef.markForCheck();
  }

  private updateFormControl(): void {
    // const id = this.tree.treeModel.getActiveNode()?.id;
    this.formControl.setValue(this.selectedNodeId);
    this.formControl.markAsDirty();
    this.onChange(this.selectedNodeId);
    this.changeDetectorRef.markForCheck();
  }

  private getSourceData = async (): Promise<void> => {
    if(this.field.props['treeData']) {
      this.nodes = [this.field.props['treeData']];
      this.changeDetectorRef.markForCheck();
      return;
    }
    const treeAttachmentConfig = this.field?.props?.['treeAttachment'] as TreeAttachment;
    if (!treeAttachmentConfig) {
      console.warn('No treeAttachment configuration found for tree level field.');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }
    
    const clientId = this.contextService.getCurrentClientId();
    if (!clientId) {
      console.error('No client ID found in context service.');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const subsdiaryId= this.contextService.getCurrentSubsidiaryId();
    if (!subsdiaryId) {
      console.error('No subsidiary ID found in context service.');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    const url = treeAttachmentConfig.sourceApi
      .replace('{{apiUrl}}', environment.apiUrl) // Replace with actual API URL
      .replace('{{clientId}}', clientId);   // Replace with actual client ID

    const response  = await this.http.get<ApiResponse<any>>(url).toPromise();
    if(!response || !response.success || !response.data) {
      console.error('Failed to load tree data from source API or targetIdField not found in response.');
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }
    let tree = this.miscService.getDeepChildValue(response.data, treeAttachmentConfig.targetIdField);
    if (!tree) {
      console.error(`Target ID field "${treeAttachmentConfig.targetIdField}" not found in API response data.`);
      this.isLoading = false;
      this.changeDetectorRef.markForCheck();
      return;
    }

    if( treeAttachmentConfig.chRoot ) {
      try {
        const obj = eval(treeAttachmentConfig.chRoot.object);
        const chRoot = obj ? obj[treeAttachmentConfig.chRoot.prop] : null;
        if (chRoot) {
          tree = this.miscService.findNodeInTree(tree, chRoot);
        }
      } catch (error) {
        console.error('Error evaluating chRoot object expression:', error);
        this.isLoading = false;
        this.changeDetectorRef.markForCheck();
        return;
      }
    }

    this.nodes = [tree];
    this.isLoading = true;
    this.changeDetectorRef.markForCheck();
  }

  private getActionMapping(): IActionMapping {
    return {
      mouse: {
        click: (tree, node, $event) => {
          if (!this.canSelectNode(node)) {
            return;
          }
          // this.treeState.selectedNodeIds = this.treeState.selectedNodeIds || {};
          // this.treeState.selectedNodeIds[node.id] = true;
          this.selectedNodeId = node.id;
          this.updateFormControl();
          this.changeDetectorRef.markForCheck();
        },
      },
    };
  }

  public onlyLeafSelectable(): boolean {
    return this.field.props?.['onlyLeafSelectable'] === true;
  }

  public canSelectNode(node: TreeNode): boolean {
    return !this.onlyLeafSelectable() || this.isLeafNode(node);
  }

  private isLeafNode(node: TreeNode): boolean {
    const hasChildren = typeof (node as any).hasChildren === 'function'
      ? (node as any).hasChildren()
      : (node?.data?.children?.length || node?.children?.length) > 0;
    return !hasChildren;
  }

  public getLevelAsText(): string {
    const path: TreeNodeData[] = [];
    this.miscService.getPathToNode(this.nodes[0], this.selectedNodeId || '', path);
    return path.map(l => l.name).join(' / ');
  }

  public expandRootNode(): void {
    if (this.nodes.length > 0) {
      this.treeState.expandedNodeIds = this.treeState.expandedNodeIds || {};
      this.treeState.expandedNodeIds = { ...this.treeState.expandedNodeIds, [this.nodes[0].id]: true };
      this.changeDetectorRef.markForCheck();
    }
  }

  public getNodeNameById(): string {
    if (!this.selectedNodeId) return '';
    const node = this.miscService.findNodeInTree(this.nodes[0], this.selectedNodeId);
    return node ? node.name : '';
  }
}